import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/service/auth.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

@Injectable()
export class StatusErrorInterceptor implements HttpInterceptor {
  private readonly toastrService = inject(ToastrService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          req.url.includes('public/time-clock') ||
          req.url.includes('purchase-request/external')
        ) {
          // Páginas públicas (sem autenticação) precisam da mensagem real do
          // backend (ex: "Esta cotação foi cancelada.") — não do texto
          // genérico por status nem do toast, que não fazem sentido pro
          // fornecedor nessas telas.
          return throwError(() => error);
        }

        if (
          error.status === 401 &&
          (req.url.includes('authentication/login') ||
            req.url.includes('authentication/validate-token') ||
            req.url.includes('authentication/switch-client') ||
            req.url.includes('authentication/select-client'))
        ) {
          return throwError(
            () => new Error(error.error?.message ?? 'Erro de autenticação')
          );
        }

        let errorMessage = error.error?.message ?? 'Erro desconhecido';

        switch (error.status) {
          case 400:
            errorMessage = 'Requisição inválida';
            break;
          case 401:
            errorMessage = 'Não autorizado';
            break;
          case 403:
            errorMessage = 'Acesso proibido';
            break;
          case 404:
            errorMessage = 'Recurso não encontrado';
            break;
          case 422: {
            // 422 carrega mensagem de regra de negócio do backend (pt-BR,
            // voltada ao usuário) — exibi-la em vez do genérico.
            const backendMessage = error.error?.message;
            errorMessage = Array.isArray(backendMessage)
              ? backendMessage.join(' ')
              : (backendMessage ?? 'Dados inválidos');
            break;
          }
          case 500:
            errorMessage = 'Erro interno do servidor';
            break;
        }

        if (error.status === 401) {
          return this.authService.validateToken().pipe(
            switchMap(response => {
              if (response.status === 200) {
                this.toastrService.error(
                  `Status ${error.status}: ${errorMessage}`
                );
                return throwError(() => new Error(errorMessage));
              } else {
                this.router.navigate(['/auth']);
                this.toastrService.error(
                  'Token inválido. Faça o login novamente.',
                  'Erro'
                );
                return throwError(() => new Error('Token inválido'));
              }
            }),
            catchError(() => {
              this.router.navigate(['/auth']);
              return throwError(() => new Error('Falha ao validar token'));
            })
          );
        }

        if (
          error.status === 422 &&
          Array.isArray(error.error?.ineligibleExpenses)
        ) {
          return throwError(() =>
            Object.assign(new Error(errorMessage), {
              status: error.status,
              error: error.error,
            })
          );
        }

        this.toastrService.error(`Status ${error.status}: ${errorMessage}`);
        return throwError(() => new Error(errorMessage));
      })
    );
  }
}
